import React, { useEffect, useState } from "react";
import { M } from "./tokens";
import { PROOF } from "./machines";
import { useBrand } from "./BrandContext";
import { api } from "../../lib/api";

/* ══════════════════════════════════════════════════════════════
   AD STUDIO
   Generates a real static ad on demand through Higgsfield.
   The whole section stays hidden until the backend reports the
   integration is configured, so there is never a dead button.
   ══════════════════════════════════════════════════════════════ */

const ACCENT = "#4C9AFF";

const HOOKS = [
  "My doctor is shocked.",
  "Day 28 and steady.",
  "One capsule. Not six.",
  "Stop stacking. Start a system.",
];

export function useIntegrations() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let live = true;
    api
      .get("/integrations")
      .then(({ data }) => live && setS(data))
      .catch(() => live && setS({ higgsfield: false, ad_library: false }));
    return () => {
      live = false;
    };
  }, []);
  return s;
}

export default function AdStudio() {
  const integrations = useIntegrations();
  const { brand } = useBrand();
  const [hook, setHook] = useState(HOOKS[0]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  /* poll the job until it lands */
  useEffect(() => {
    const done = (j) => j?.slots?.length && j.slots.every((x) => ["completed", "failed", "nsfw"].includes(x.status));
    if (!job?.id || done(job)) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/ad-creatives/${job.id}`);
        setJob(data);
        if (done(data)) setBusy(false);
      } catch {
        setErr("Lost the job. Try again.");
        setBusy(false);
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [job]);

  if (!integrations?.higgsfield) return null;

  const generate = async () => {
    setErr("");
    setBusy(true);
    setJob({ status: "submitting", images: [] });
    try {
      const { data } = await api.post("/ad-creatives", {
        product_image_url: PROOF.hashi,
        hook_line: hook,
        brand: brand || PROOF.brand,
        product_name: "HashiAid",
        machine_id: "meta",
        has_proof: false,
        has_confirmed_offer: false,
        regulated: true,
      });
      setJob(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const clean = typeof d === "string" && d.length < 140 && !/<|cloudflare|origin web server/i.test(d) ? d : "";
      setErr(clean || "That did not go through. Try it again in a moment.");
      setBusy(false);
    }
  };

  const slots = job?.slots || [];
  const finished = slots.filter((x) => ["completed", "failed", "nsfw"].includes(x.status)).length;
  const status = !job ? "idle" : slots.length && finished === slots.length ? "completed" : job.status || "generating";
  const pct = slots.length ? Math.max(8, (finished / slots.length) * 100) : busy ? 8 : 0;

  return (
    <section className="border-b" style={{ borderColor: M.border }} data-testid="ad-studio">
      <div className="mx-auto grid w-full max-w-[1280px] gap-10 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1fr_1fr] lg:gap-16">
        <div>
          <div className="f-mono mb-4 text-[10px] uppercase tracking-[0.22em]" style={{ color: ACCENT }}>
            Watch it work
          </div>
          <h2 className="f-grotesk max-w-[22ch] text-[28px] font-bold leading-[1.05] tracking-[-0.03em] sm:text-[42px]">
            Make one right now.
          </h2>
          <p className="mt-5 max-w-[52ch] text-[15px] leading-[1.8]" style={{ color: M.muted }}>
            Pick a hook and it produces four different ads on the real product photography: one for the scroll stopper, one for
            the benefit, one for the sceptic, one for the buyer. Four formats, four angles, one product truth.
          </p>

          <div className="mt-7 flex flex-wrap gap-2" data-testid="ad-studio-hooks">
            {HOOKS.map((h) => {
              const on = h === hook;
              return (
                <button
                  key={h}
                  onClick={() => setHook(h)}
                  disabled={busy}
                  data-testid={`ad-studio-hook-${HOOKS.indexOf(h)}`}
                  className="focus-ring rounded-lg border px-3.5 py-2.5 text-[12.5px] transition-colors disabled:opacity-50"
                  style={{
                    borderColor: on ? ACCENT : M.border,
                    background: on ? `${ACCENT}12` : M.surface,
                    color: on ? M.ink : M.muted,
                  }}
                  aria-pressed={on}
                >
                  {h}
                </button>
              );
            })}
          </div>

          <button
            onClick={generate}
            disabled={busy}
            data-testid="ad-studio-generate"
            className="focus-ring f-mono mt-7 rounded px-6 py-3.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] disabled:opacity-60"
            style={{ background: ACCENT, color: "#04121F" }}
          >
            {busy ? "Producing" : "Produce the ad"}
          </button>
          {err && (
            <p className="f-mono mt-4 text-[10px]" style={{ color: M.danger }} data-testid="ad-studio-error">
              {err}
            </p>
          )}
          <p className="f-mono mt-4 text-[9px] leading-relaxed" style={{ color: M.dim }}>
            Product pixels are preserved. It may not invent a claim, a price or a badge.
          </p>
        </div>

        <div className="rounded-2xl border p-5" style={{ borderColor: M.border, background: M.surface }} data-testid="ad-studio-canvas">
          <div className="f-mono flex items-center gap-2.5 text-[9px] uppercase tracking-[0.16em]" style={{ color: M.dim }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: status === "completed" ? M.ok : ACCENT }} />
            {status === "idle" ? "waiting on you" : `${status.replace("_", " ")}${slots.length ? ` · ${finished} of ${slots.length}` : ""}`}
          </div>
          <div className="mt-3 h-[3px] w-full overflow-hidden rounded-full" style={{ background: M.border }}>
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: status === "completed" ? M.ok : ACCENT }} />
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {(slots.length ? slots : [null, null, null, null]).map((slot, i) => {
              const src = slot?.images?.[0];
              return (
                <div
                  key={i}
                  className="overflow-hidden rounded-xl border"
                  style={{ borderColor: M.border, background: M.surface2 }}
                  data-testid={`ad-studio-slot-${i}`}
                >
                  <div className="flex items-center justify-center" style={{ minHeight: 190 }}>
                    {src ? (
                      <img src={src} alt={hook} className="h-full w-full object-contain" data-testid={`ad-studio-image-${i}`} />
                    ) : (
                      <span className="f-mono px-4 text-center text-[9px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>
                        {slot ? (slot.status === "failed" ? "rejected" : "rendering") : "nothing produced yet"}
                      </span>
                    )}
                  </div>
                  {slot && (
                    <div className="f-mono border-t px-3 py-2 text-[8.5px] uppercase tracking-[0.12em]" style={{ borderColor: M.border, color: ACCENT }}>
                      {slot.format_key?.replace(/_/g, " ")} · {slot.awareness_level?.replace(/_/g, " ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {job?.error && (
            <p className="f-mono mt-3 text-[9.5px]" style={{ color: M.danger }}>
              Generation failed. Nothing was charged for a failed render.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
